// Copyright (c) Docugami, Inc. All rights reserved.

import { ReactNode, useMemo } from "react";
import styled from "styled-components";
import { ExplorerInfoText } from "../ExplorerStyles";
import { appBackgroundGrey, greyText, primaryTheme } from "components/shell/presentation/AppStyles";
import { IGroup } from "@fluentui/react";
import { CatalogItemPreview, CatalogPlaceholderPreview } from "./CatalogPreview";
import { CatalogItemsList } from "./CatalogItemsList";

export interface ICatalogItem {
    id: string;
    name: string;
    description: ReactNode;
    primaryAction?: JSX.Element;
    statusProps?: CatalogItemStatusProps;
}

export interface CatalogItemStatusProps {
    label: string;
    icon?: JSX.Element;
}

export interface ICatalogItemGroup {
    id: string;
    name: string;
    items: ICatalogItem[];
}

const GridContentContainer = styled.div`
    display: flex;
    flex-direction: row;
    height: 100%;
    width: 100%;
`;

const ListContainer = styled.div`
    border-right: 1px solid ${greyText};
    height: 100%;
    background-color: ${appBackgroundGrey};

    display: flex;
    flex-direction: column;
    height: 100%;
    width: 500px;
`;

const CatalogListHeader = styled.div`
    display: flex;
    padding: 10px;
    box-sizing: border-box;
    background-color: ${primaryTheme.palette.white};
    border-bottom: 1px solid ${greyText};
`;

const TitleText = styled(ExplorerInfoText)`
    font-size: 32px;
    line-height: 40px;
`;

const CatalogPreviewContainer = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    min-height: 0;
`;

interface CatalogProps {
    title: string;
    groupItems: ICatalogItemGroup[];
    placeholderText?: string;
    isLoading?: boolean;
    onSelectItem?: (id: string) => void
    selectedItemId?: string;
}

export const Catalog = (props: CatalogProps): JSX.Element => {
    const { groups, items } = useMemo(() => {
        const groups: IGroup[] = [];
        const items: ICatalogItem[] = [];

        props.groupItems.forEach(group => {
            groups.push({
                key: group.id,
                name: group.name,
                startIndex: items.length,
                count: group.items.length
            });
            items.push(...group.items);
        });

        return { groups, items };
    }, [props.groupItems])

    function getPreviewForCatalogItem() {
        const selectedItem = items.find(x => x.id === props.selectedItemId);
        if (!!selectedItem) {
            return <CatalogItemPreview item={selectedItem} />
        } else {
            // Placeholder preview content
            return <CatalogPlaceholderPreview placeholderText={props.placeholderText} />
        }
    }

    return (
        <GridContentContainer>
            <ListContainer>
                <CatalogListHeader>
                    <TitleText>{props.title}</TitleText>
                </CatalogListHeader>
                <CatalogItemsList
                    groups={groups}
                    items={items}
                    isLoading={props.isLoading}
                    selectedItemId={props.selectedItemId || ''}
                    onSelectItem={(id) => {
                        props.onSelectItem?.(id);
                    }} />
            </ListContainer>
            <CatalogPreviewContainer>{getPreviewForCatalogItem()}</CatalogPreviewContainer>
        </GridContentContainer>
    )
}
